<template>
    <div>
        <div class="tk-card-header">
            <h2>Referral Details</h2>
            <div class="row items-center q-gutter-sm">
                <q-chip v-if="engagement.referral_form_type" dense square color="blue-1" text-color="blue-10">
                    {{ formLabel }}
                </q-chip>
                <slot name="actions" />
            </div>
        </div>

        <div class="referral-source-field">
            <span>Referral source</span>
            <strong>{{ engagement.referralSource }}</strong>
        </div>

        <template v-if="engagement.referral_form_type">
            <q-banner v-if="parseError" class="bg-red-1 text-negative q-mt-md" rounded>
                {{ parseError }}
            </q-banner>
            <template v-else>
                <section v-if="referralSpecificInformation.length" class="referral-specific-section">
                    <h3>Referral-specific information</h3>
                    <div class="referral-specific-grid">
                        <div
                            v-for="field in referralSpecificInformation"
                            :key="field.originalFieldKey"
                            class="referral-specific-field"
                        >
                            <span>{{ field.label || field.originalFieldKey }}</span>
                            <strong>{{ displayReferralValue(field.value) }}</strong>
                        </div>
                    </div>
                </section>

                <q-expansion-item
                    v-if="reviewGroups.length"
                    class="reviewed-information"
                    icon="fact_check"
                    label="Review all extracted information"
                    header-class="text-primary"
                    dense
                >
                    <div class="reviewed-information-content">
                        <div
                            v-for="group in reviewGroups"
                            :key="group.key"
                            class="reviewed-group"
                        >
                            <h4>{{ group.label }}</h4>
                            <div class="reviewed-grid">
                                <div
                                    v-for="field in group.fields"
                                    :key="field.originalFieldKey"
                                    class="reviewed-field"
                                >
                                    <span>{{ field.label || field.originalFieldKey }}</span>
                                    <strong>{{ displayReferralValue(field.reviewedValue) }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </q-expansion-item>
            </template>
        </template>
    </div>
</template>

<script>
import {
    displayReferralValue,
    hasReferralValue,
} from "@/services/util/referralWorkflow";

const parseJsonArray = (value, fieldName) => {
    if (value == null || value === "") return [];
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) {
        throw new Error(`${fieldName} must contain a JSON array`);
    }
    return parsed;
};

const parseExtraction = (value) => {
    if (value == null || value === "") {
        throw new Error("referral_extraction is required when referral_form_type is present");
    }
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("referral_extraction must contain a JSON object");
    }
    if (!Array.isArray(parsed.fields)) {
        throw new Error("referral_extraction.fields must contain a JSON array");
    }
    return parsed;
};

export default {
    name: "ReferralEngagementInformation",
    props: {
        engagement: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            extractionFields: [],
            referralSpecificInformation: [],
            formName: null,
            parseError: null,
        };
    },
    computed: {
        formLabel() {
            if (this.formName) return this.formName;
            return String(this.engagement.referral_form_type || "")
                .split("_")
                .filter(Boolean)
                .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
                .join(" ");
        },
        reviewGroups() {
            const fields = this.extractionFields.filter(field => hasReferralValue(field.reviewedValue));
            const groups = [
                {
                    key: "person",
                    label: "Person details",
                    fields: fields.filter(field => field.destination?.entity === "person"),
                },
                {
                    key: "engagement",
                    label: "Client Engagement",
                    fields: fields.filter(field => field.destination?.entity === "engagement"),
                },
                {
                    key: "referral",
                    label: "Referral-specific information",
                    fields: fields.filter(field => !field.destination),
                },
            ];
            return groups.filter(group => group.fields.length);
        },
    },
    watch: {
        engagement: {
            immediate: true,
            handler() {
                this.loadReferralInformation();
            },
        },
    },
    methods: {
        displayReferralValue,
        loadReferralInformation() {
            if (!this.engagement.referral_form_type) {
                this.extractionFields = [];
                this.referralSpecificInformation = [];
                this.formName = null;
                this.parseError = null;
                return;
            }
            try {
                const extraction = parseExtraction(this.engagement.referral_extraction);
                this.extractionFields = extraction.fields;
                this.formName = extraction.formName || null;
                this.referralSpecificInformation = parseJsonArray(
                    this.engagement.referral_additional_information,
                    "referral_additional_information"
                ).filter(field => hasReferralValue(field.value));
                this.parseError = null;
            } catch {
                this.extractionFields = [];
                this.referralSpecificInformation = [];
                this.formName = null;
                this.parseError = (
                    "Referral information could not be displayed. "
                    + "Please ask an administrator to check this Client Engagement."
                );
            }
        },
    },
};
</script>

<style scoped>
.referral-source-field {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 9px 0;
    border-bottom: 1px solid rgba(47, 62, 70, 0.06);
}

.referral-source-field span {
    color: #748078;
    font-size: 0.8rem;
    line-height: 1.25;
}

.referral-source-field strong {
    color: #3c4b45;
    font-size: 0.93rem;
    font-weight: 550;
    line-height: 1.3;
    overflow-wrap: anywhere;
}

.referral-specific-section {
    margin: 18px 0 8px;
}

.referral-specific-section h3,
.reviewed-group h4 {
    margin: 0;
    color: #46544e;
    font-weight: 600;
    letter-spacing: 0.025em;
}

.referral-specific-section h3 {
    margin-bottom: 10px;
    font-size: 0.95rem;
}

.referral-specific-grid,
.reviewed-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 18px;
}

.referral-specific-field,
.reviewed-field {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 9px 0;
    border-bottom: 1px solid rgba(47, 62, 70, 0.06);
}

.referral-specific-field span,
.reviewed-field span {
    color: #748078;
    font-size: 0.8rem;
    font-weight: 450;
    letter-spacing: 0.01em;
    line-height: 1.25;
    text-transform: none;
}

.referral-specific-field strong,
.reviewed-field strong {
    color: #3c4b45;
    font-size: 0.93rem;
    font-weight: 550;
    line-height: 1.3;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.reviewed-information {
    margin-top: 6px;
}

.reviewed-information-content {
    padding: 8px 12px 14px 40px;
}

.reviewed-group + .reviewed-group {
    margin-top: 18px;
}

.reviewed-group h4 {
    margin-bottom: 4px;
    font-size: 0.9rem;
}

@media (max-width: 700px) {
    .referral-specific-grid,
    .reviewed-grid {
        grid-template-columns: 1fr;
    }

    .reviewed-information-content {
        padding-left: 12px;
    }
}

@media (max-width: 480px) {
    .referral-source-field,
    .referral-specific-field,
    .reviewed-field {
        grid-template-columns: 1fr;
        gap: 2px;
    }
}
</style>
